import { motion } from 'framer-motion';
import { FileImage, FileVideo, FileAudio, FileText, File as FileIcon, Globe, Share2, Image as ImageIcon } from 'lucide-react';
import { StatusBadge } from './VisualAtoms';
import { TONE } from '../theme/adminVisualSystem';

const TYPE_ICON = {
  image: FileImage,
  video: FileVideo,
  audio: FileAudio,
  pdf: FileText,
  other: FileIcon,
};

function statusForArtistWork(status) {
  // adminVisualSystem doesn't have artist_works tone map yet; map locally
  const map = {
    submitted: TONE.cyan,
    approved: TONE.green,
    hidden: TONE.muted,
    archived: TONE.red,
  };
  const tone = map[status] || TONE.neutral;
  return tone;
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * ArtistWorkCard — grid card for an artist_work row.
 * Heavy media is NEVER auto-loaded:
 *   - images: lazy <img loading="lazy" />
 *   - videos: poster only (or icon if no thumbnail)
 *   - others: icon + filename
 * Click → opens ArtistWorkPreviewModal via parent.
 */
export default function ArtistWorkCard({ work, onClick }) {
  const Icon = TYPE_ICON[work.file_type] || FileIcon;
  const tone = statusForArtistWork(work.status);
  const usageActive = !!(work.usage_social_media || work.usage_gallery_exhibition || work.usage_website);
  const thumb = work.thumbnail_url;

  const showImage = (work.file_type === 'image') && (thumb || work.file_url);
  const showVideoPoster = (work.file_type === 'video') && thumb;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-clickable="true"
      data-testid={`work-card-${work.id}`}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
      className="text-left relative border border-[#1a1a1a] bg-[#0d0d0d] hover:border-[#90E0EF]/40 transition-colors overflow-hidden flex flex-col"
      style={{ cursor: 'pointer' }}
    >
      {/* Preview area */}
      <div className="aspect-[4/3] bg-[#050505] flex items-center justify-center relative overflow-hidden border-b border-[#1a1a1a]">
        {showImage ? (
          <img
            src={thumb || work.file_url}
            alt={work.title || work.file_name || 'work'}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : showVideoPoster ? (
          <>
            <img src={thumb} alt="poster" loading="lazy" className="w-full h-full object-cover" />
            <span className="absolute bottom-2 right-2 text-[9px] font-mono text-white bg-black/60 px-1.5 py-0.5 uppercase tracking-[0.18em]">
              VIDEO
            </span>
          </>
        ) : (
          <div className="text-center px-4">
            <Icon className="w-8 h-8 text-[#444] mx-auto" />
            <p className="text-[10px] font-mono text-[#666] mt-2 tracking-[0.18em] uppercase">
              {(work.file_type || 'file').toUpperCase()}
            </p>
          </div>
        )}

        {/* Usage chips overlay */}
        {usageActive && (
          <div className="absolute top-2 left-2 flex gap-1">
            {work.usage_social_media && (
              <span className="w-5 h-5 bg-black/70 border border-[#333] flex items-center justify-center" title="Social Media">
                <Share2 className="w-2.5 h-2.5 text-[#90E0EF]" />
              </span>
            )}
            {work.usage_gallery_exhibition && (
              <span className="w-5 h-5 bg-black/70 border border-[#333] flex items-center justify-center" title="Gallery Exhibition">
                <ImageIcon className="w-2.5 h-2.5 text-[#7ed4a0]" />
              </span>
            )}
            {work.usage_website && (
              <span className="w-5 h-5 bg-black/70 border border-[#333] flex items-center justify-center" title="Website">
                <Globe className="w-2.5 h-2.5 text-[#c89dff]" />
              </span>
            )}
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="p-3 space-y-1.5 flex-1">
        <p className="text-xs font-mono text-white truncate">
          {work.title || work.file_name || 'Untitled'}
        </p>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-mono text-[#666] truncate">
            {(work.file_type || 'file').toUpperCase()}
            {work.file_size ? ` · ${formatBytes(work.file_size)}` : ''}
          </p>
          <StatusBadge status={work.status || 'submitted'} className="!text-[9px]" />
        </div>
      </div>
    </motion.button>
  );
}
